<template>
	<page-container :page="page">
		<heading-image
			v-if="page && page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content>
			<template #content>
				<div class="columns column6">
					<h1>{{ page.title || page.header }}</h1>
					<div v-parse-links v-html="page.content" />
					<div v-parse-links v-html="page.rightContent" />
					<book-button v-if="page.bookingID" :roomid="page.bookingID" class="button">
						{{ $t('bookThisRoom') }}
					</book-button>
				</div>
			</template>
			<template #rightContent>
				<div class="columns column6">
					<div class="room-icons-wrapper">
						<h3>{{ defaults[locale].rooms.thisRoomContains }}</h3>
						<div class="room-icons">
							<div v-if="page.pax" class="icon-holder">
								<font-awesome-icon :icon="`fal fa-users`" size="1x" />
								<span>{{ page.pax }}</span>
							</div>
							<div v-if="page.surface" class="icon-holder">
								<font-awesome-icon :icon="`fal fa-ruler-combined`" size="1x" />
								<span>{{ page.surface }}</span>
							</div>
							<div v-if="page.lounge" class="icon-holder">
								<font-awesome-icon :icon="`fal fa-couch`" size="1x" />
								<span>{{ page.lounge }}</span>
							</div>
							<div v-for="facility in page.facilities" :key="facility.id" class="icon-holder">
								<font-awesome-icon :icon="`fal fa-${facility.icon}`" size="1x" />
								<span>{{ facility.content }}</span>
							</div>
						</div>
					</div>
				</div>
			</template>
		</main-content>

		<div class="main-content" :class="page.gallery && page.gallery.length ? 'gallery-section' : 'button-section'">
			<div class="row gallery-grid">
				<gallery-item
					v-if="page.galleryVideoEmbedCode"
					:item="{
						type: 'video',
						embedCode: page.galleryVideoEmbedCode,
						images: page.galleryVideoThumbnails,
					}"
				/>
				<gallery-item v-for="(item, index) in page.gallery" :key="index" :item="item" />
			</div>
			<div class="row">
				<div class="columns column12">
					<button-container>
						<nuxt-link :to="localePath('rooms')" class="button-arrow" style="margin: 0 auto">
							{{ defaults[locale].rooms.more }}
						</nuxt-link>
					</button-container>
				</div>
			</div>
		</div>

		<contentblocks v-if="page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>

		<newsletter
			v-if="page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

onMounted(() => {
	Fancybox.bind('[data-fancybox]', {});
});
</script>

<style lang="scss" scoped>
.gallery-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 60px;
	padding: 0 30px;
	margin-bottom: 25px;
}

.gallery-section {
	padding-top: 0;

	img {
		width: 100%;
		padding: 30px 0;
	}
}

.button-section {
	padding: 0 0 100px;
}

.room-icons {
	display: flex;
	flex-flow: wrap row;
	justify-content: flex-start;
	align-items: center;
	gap: 0 10px;
	width: 100%;

	.room-icon {
		margin: 0 20px 0 0;
	}

	span {
		font-size: 15px;
	}

	svg {
		color: var(--primary-color);
		margin-right: 5px;
		font-size: 18px;
	}
}

.room-icons-wrapper {
	border: 1px solid var(--primary-color);
	color: var(--body-color);
	padding: 50px 60px;
	background: url('~/assets/images/promoblocks-bg-pattern.jpg') no-repeat center center;
	background-size: cover;
	width: 100%;

	h3 {
		margin: 0 0 20px;
	}

	.room-icons {
		.icon-holder {
			width: 49%;
			margin: 5px 0;

			svg {
				margin: 0 15px 0 0;
			}
		}
	}
}

.icon-holder i,
.icon-holder svg {
	margin: 0 5px 0 0;
}

@media (max-width: 960px) {
	.gallery-grid {
		grid-template-columns: 1fr;
	}

	.room-icons-wrapper {
		margin: 35px auto 0;
	}
}

@media (max-width: 780px) {
	.room-icons-wrapper {
		.room-icons {
			.icon-holder {
				width: 100%;
			}
		}
	}
}
</style>
